import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { identityServices } from '../identityServices'
import { VerifyEmailUpdatePayload } from '../identityTypes'
import { ApiErrorCode, ErrorDetail } from '@/api/apiTypes'
import { mapAxiosErrorToApiError } from '@/utils'

interface UseVerifyEmailUpdateOptions {
  onSuccess?: (data: unknown) => void
  onError?: (error: { errorMessage?: string; code?: string }) => void
}

export const useVerifyEmailUpdate = (
  options?: UseVerifyEmailUpdateOptions,
) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutation = useMutation<
    void,
    ErrorDetail,
    VerifyEmailUpdatePayload
  >({
    mutationFn: identityServices.verifyEmailUpdate,
    onSuccess: (data) => {
      // Refresh user data after successful email verification
      queryClient.invalidateQueries({ queryKey: ['identity', 'me'] })

      options?.onSuccess?.(data)
    },
    onError: (err) => {
      const { code } = mapAxiosErrorToApiError(err)

      let errorMessage = t('api:errors.somethingWrong')

      if (code === ApiErrorCode.TOO_MANY_ATTEMPTS) {
        errorMessage = t('api:errors.manyAttempts')
      }

      if (code === ApiErrorCode.CODE_NOT_VERIFIED) {
        errorMessage = t('api:validationErrors.invalidCode')
      }

      if (code === ApiErrorCode.EMAIL_ALREADY_EXISTS_ERROR) {
        errorMessage = t('api:errors.emailAlreadyInUse')
      }

      options?.onError?.({
        errorMessage,
        code,
      })
    },
  })

  function verifyEmailUpdate(payload: VerifyEmailUpdatePayload) {
    mutation.mutate(payload)
  }

  return {
    verifyEmailUpdate,
    isLoading: mutation.isPending,
    ...mutation,
  }
}
